import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { STORAGE_KEYS, getStorageItem, setStorageItem } from '@/lib/storage';
import {
  getUserThemePreference,
  saveUserThemePreference,
} from '@/services/userPreferencesService';
import { ThemeContext, type Theme } from '@/components/theme-context';

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { user, loading: authLoading } = useAuth();
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = getStorageItem<Theme>(STORAGE_KEYS.THEME);
    return savedTheme || 'light';
  });
  const [hydratedUserId, setHydratedUserId] = useState<string | null>(null);
  const skipNextPersistRef = useRef<Theme | null>(null);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    setStorageItem(STORAGE_KEYS.THEME, theme);
  }, [theme]);

  useEffect(() => {
    let cancelled = false;

    const hydrateThemePreference = async () => {
      if (authLoading) return;

      if (!user?.id) {
        setHydratedUserId(null);
        return;
      }

      try {
        const persistedTheme = await getUserThemePreference(user.id);
        if (cancelled) return;

        if (persistedTheme) {
          skipNextPersistRef.current = persistedTheme;
          setTheme(persistedTheme);
        }
      } catch (error) {
        console.warn('[ThemeProvider] Failed to hydrate theme preference', error);
      } finally {
        if (!cancelled) {
          setHydratedUserId(user.id);
        }
      }
    };

    void hydrateThemePreference();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id]);

  useEffect(() => {
    if (!user?.id || hydratedUserId !== user.id) return;

    if (skipNextPersistRef.current === theme) {
      skipNextPersistRef.current = null;
      return;
    }

    void saveUserThemePreference(user.id, theme).catch((error) => {
      console.warn('[ThemeProvider] Failed to persist theme preference', error);
    });
  }, [hydratedUserId, theme, user?.id]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
